/**
 * 身份证复合字段（姓名 + 证件号 + 人像面 / 国徽面采集 + OCR）
 * -----------------------------------------------------------------------------
 * 为什么是一个组件而不是三个字段：
 *   姓名、证件号、两张证件照在业务上是**同一件东西**的四份投影 —— 人像面拍完
 *   OCR 出来就是姓名与号码，拆成三段渲染会让用户重复劳动，也让"到底该先填哪个"
 *   变成一道阅读题。所以渲染器把 schema 里的 `name`（text/system）与
 *   `idCardNo`（idcard）两个字段合成一张卡，卡内只走一次采集。
 *
 * 口径：
 *   ① **允许相册**——这是「把已有证件拍清楚」，不是活体取证；与职业证件照
 *      （`KYC-301` 必须现拍）是两条规则，别混。
 *   ② 国徽面**只取裁剪图、不做字段识别**：调供应商同一个接口（`id_card_side=back`），
 *      要的只是 `detect_card` 产出的**扶正 + 去背景**那张。为什么要多跑一次接口而不是
 *      直接存原片 —— 原片带桌面背景、可能是歪的、可能只占画面一角，那种图留档等于没留。
 *      它不在五步链内（不产生核验记录、不参与准入判定），见 `scanIdCardBack`。
 *   ③ OCR 下发的证件号是**明文**（服务端已按本人鉴权），识别成功后直接回填姓名与
 *      证件号 —— 学员不用照着掩码手敲 18 位，也就不会抄错。
 *   ④ **头像切图是这一步的产出，不是副产品**：它是 ④ 人像比对唯一正确的参照图
 *      （整卡图里人脸只占约 1/6）。所以识别成功后必须把服务端切好的头像**显示出来**，
 *      没切到也要明说 —— 静默退回整卡图会让比对分数莫名偏低。
 *      同理，预览要用服务端**矫正裁剪后**的图，而不是本地那张还没校正的原片。
 *   ⑤ **性别与民族也在这张卡里**（2026-10-21）：证件上明明印着，再让学员去别的分组
 *      手点一遍只是多一个抄错的机会。它们按「谁产出这个值」归到身份证卡 ——
 *      与 `name` / `idCardNo` 同一条理由。**但保留可编辑**：民族是必填项，供应商对
 *      生僻族名的识别并不稳，读不出或读错时学员必须能当场改，而不是被一个
 *      自动填入的错值卡住（错的比空的麻烦得多）。
 */
import type { FormFieldDef } from '@enroll/shared';
import { Toast } from 'antd-mobile';
import { useEffect, useState } from 'react';
import { describeApiError } from '../../api/errors';
import { kycIdCardBack, kycOcr } from '../../api/h5';
import type { KycOcrAnalysis } from '../../api/types';
import { CameraIcon, CheckIcon, PortraitIcon, SearchIcon } from '../icons';
import { CameraCapture } from '../KycFlow/CameraCapture';
import { Card, Notice } from '../layout';
import { ProcessingOverlay } from '../Processing';
import { openImagePreview } from '../zoom';
import { describeOcrAnalysis } from './idcard-ocr';
import { RESIDENT_ID_TYPE } from './logic';

/** 人像面识别结果（供父级复用） */
export interface IdCardOcrResult {
  name: string;
  idCardNo: string;
}
/** 识别成功后的完整产出（含头像切图与结论摘要） */
interface OcrSnapshot {
  idCardNo: string;
  validPeriodEnd: string | null;
  /** 服务端切好的证件头像；null = 供应商没切出来 */
  avatarUrl: string | null;
  /** 服务端矫正 + 裁剪后的证件图（本地预览是未校正的原片） */
  imageUrl: string;
  analysis: KycOcrAnalysis;
}

export interface IdCardFieldProps {
  /** 跑 OCR 需要报名单 id（草稿态就有） */
  registrationId?: string;
  nameField?: FormFieldDef;
  idField: FormFieldDef;
  /**
   * 「证件类型」——它不是一张独立的表单项，而是**这张卡的第一行**：
   * 类型决定了「正面 / 反面」该长什么样、要不要跑 OCR。
   * 表单没配这个字段时就不渲染这一行（老表单没有它也能用）。
   */
  typeField?: FormFieldDef;
  /**
   * 性别 / 民族 —— 同样并进这张卡，理由见文件头 ⑤。
   *
   * 找不到这两个字段时整段不渲染：卡是老表单也能用，不因为「没配民族」就崩。
   */
  genderField?: FormFieldDef;
  ethnicityField?: FormFieldDef;
  idType?: string;
  name: string;
  idCardNo: string;
  gender?: string;
  ethnicity?: string;
  disabled?: boolean;
  nameError?: string;
  idError?: string;
  genderError?: string;
  ethnicityError?: string;
  /** 提交后编辑分级的提示（如「需复审」），与普通字段同一套 `.field-hint` 呈现 */
  hints?: string[];
  /**
   * 服务端已判这一项通过（学员离开再回来时本地采集状态已丢）。
   * 有它就别再显示「未完成」——数据明明齐了，界面不该说没做。
   */
  verified?: boolean;
  /**
   * 上次识别的产出（来自 `GET /api/h5/kyc/:id/steps` 的 `ocr`）。
   *
   * 刷新 / 换设备后本地 `ocr` 状态是空的，但服务端早就存好了头像与裁剪图 ——
   * 不传它，界面就会「已通过」却看不到任何识别结果，学员会以为白做了。
   */
  preset?: {
    imageUrl: string | null;
    avatarUrl: string | null;
    validPeriodEnd: string | null;
  } | null;
  /**
   * 已留档的国徽面（裁剪后的链接）。
   *
   * 刻意**不放进 `preset`**：`preset` 的语义是「① 人像面识别过的产出」，它的存在
   * 会让卡片显示「识别结果」区块。而国徽面独立于人像面（学员可以先拍反面），
   * 塞进去会出现「没识别过却显示识别结果、号码还是空的」。
   */
  presetBackUrl?: string | null;
  onChange: (patch: {
    name?: string;
    idCardNo?: string;
    idType?: string;
    gender?: string;
    ethnicity?: string;
  }) => void;
  /** OCR 成功时上报（父级据此在身份核验页复用这次识别） */
  onRecognized?: (result: IdCardOcrResult) => void;
}

type Busy = null | 'front' | 'back';

export function IdCardField({
  registrationId,
  nameField,
  idField,
  typeField,
  genderField,
  ethnicityField,
  idType = RESIDENT_ID_TYPE,
  name,
  idCardNo,
  gender = '',
  ethnicity = '',
  disabled = false,
  nameError,
  idError,
  genderError,
  ethnicityError,
  hints,
  verified = false,
  preset = null,
  presetBackUrl = null,
  onChange,
  onRecognized,
}: IdCardFieldProps) {
  const [frontUrl, setFrontUrl] = useState<string | null>(null);
  /** 国徽面的本地原片（上传前的即时预览，会在拿到裁剪图后被服务端那张替换） */
  const [backLocalUrl, setBackLocalUrl] = useState<string | null>(null);
  /** 国徽面裁剪后的服务端链接 */
  const [backRemoteUrl, setBackRemoteUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  /** 人像面识别产出；`null` = 还没识别成功（重拍时会先清空） */
  const [ocr, setOcr] = useState<OcrSnapshot | null>(null);
  /**
   * 哪几项是**按证件带入**的（而不是学员自己选的）。
   *
   * 只为了在卡里留一行「已按证件带入，可修改」。静默填一个值进去，学员会以为
   * 那是自己填的、或者以为系统已经替他确认过了 —— 民族尤其要说清楚：它是必填项，
   * 识别错了不核对就会一路错到后台导出表里。学员一旦动手改，这条提示自动消失。
   */
  const [autofilled, setAutofilled] = useState({ gender: false, ethnicity: false });

  /**
   * 上传/识别失败的**持久**内联提示。
   *
   * Toast 两秒就消失，学员往往还没看清「未识别到身份证」就没了 —— 槽位看着
   * 还是「重拍」，却不知道刚才那一下为什么没通过。失败原因落在卡片里，
   * 直到下一次拍摄成功才清除。
   */
  const [failHint, setFailHint] = useState<{ side: 'front' | 'back'; message: string } | null>(
    null,
  );

  // 每次换图都要释放上一张 objectURL，否则连拍十几张会一直占内存
  useEffect(() => () => void (frontUrl && URL.revokeObjectURL(frontUrl)), [frontUrl]);
  useEffect(() => () => void (backLocalUrl && URL.revokeObjectURL(backLocalUrl)), [backLocalUrl]);

  const doFront = async (image: Blob) => {
    // 重拍要先清掉上一次的识别结论：否则新的一张还没识别完，
    // 界面仍挂着上一张的头像与有效期，看上去像"这张已经通过了"
    setOcr(null);
    setFrontUrl(URL.createObjectURL(image));
    if (!registrationId) return;
    setBusy('front');
    try {
      const result = await kycOcr(registrationId, image);
      const recognizedName = result.fields.name?.trim() ?? '';
      const idCardNo = result.fields.idCardNo?.trim() ?? '';
      // 性别 / 民族：服务端已归一化（`M`|`F`、「汉族」），读不出时是 null
      const recognizedGender = result.fields.gender ?? '';
      const recognizedEthnicity = result.fields.ethnicity?.trim() ?? '';
      setOcr({
        idCardNo,
        validPeriodEnd: result.fields.validPeriodEnd,
        avatarUrl: result.avatarUrl,
        imageUrl: result.imageUrl,
        analysis: result.analysis,
      });
      // 姓名与证件号**直接回填**：识别出来还要学员照着掩码手敲 18 位，
      // 等于白拍一次 —— 抄错一位就要在 ② 被拦回来重来。
      // 性别与民族同理，但只在**读出来时**才写：读不出就保持原样，
      // 绝不拿空值把学员已经填好的东西抹掉（重拍一次反而更糟）。
      onChange({
        ...(recognizedName ? { name: recognizedName } : {}),
        ...(idCardNo ? { idCardNo } : {}),
        ...(recognizedGender ? { gender: recognizedGender } : {}),
        ...(recognizedEthnicity ? { ethnicity: recognizedEthnicity } : {}),
      });
      setAutofilled({
        gender: Boolean(recognizedGender),
        ethnicity: Boolean(recognizedEthnicity),
      });
      onRecognized?.({ name: recognizedName, idCardNo });
      // 头像没切出来时不该报"已识别"就完事 —— 那是后面比对掉分的伏笔
      setFailHint(null);
      Toast.show({
        icon: result.avatarUrl ? 'success' : 'fail',
        content: result.avatarUrl ? '人像面已识别' : '已识别，但未能提取证件头像',
      });
    } catch (error) {
      setFailHint({ side: 'front', message: describeApiError(error) });
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(null);
    }
  };

  /**
   * 国徽面：拍完立刻上传，换回**裁剪后的干净图**。
   *
   * 失败时要把本地预览一起撤掉 —— 留着一张未裁剪的原片、状态胶囊却写着「已采集」，
   * 正是这次要消灭的那种假象（学员以为存住了，实际什么都没存）。
   */
  const doBack = async (image: Blob) => {
    // 没有报名单 id 就没有留档的去处：此时**不能**留本地预览，
    // 否则状态胶囊会跳到「已采集」而服务端什么都没有 —— 正是要消灭的那种假象
    if (!registrationId) {
      Toast.show({ icon: 'fail', content: '报名单尚未创建，请稍后重试' });
      return;
    }
    setBackLocalUrl(URL.createObjectURL(image));
    setBusy('back');
    try {
      const result = await kycIdCardBack(registrationId, image);
      setBackRemoteUrl(result.imageUrl);
      setFailHint(null);
      Toast.show({ icon: 'success', content: '国徽面已裁剪留档' });
    } catch (error) {
      setBackLocalUrl(null);
      setFailHint({ side: 'back', message: describeApiError(error) });
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(null);
    }
  };

  const recognized = ocr !== null || preset !== null;
  /** 国徽面优先用裁剪后的服务端图，其次用上次回显的，最后才用本地未裁剪原片 */
  const backSrc = backRemoteUrl ?? presetBackUrl ?? backLocalUrl;
  const hasBoth = Boolean(frontUrl) && Boolean(backSrc);
  const presentation = ocr ? describeOcrAnalysis(ocr.analysis) : null;
  /** 头像 / 有效期优先用本次识别的，其次用服务端回显的 */
  const avatarUrl = ocr?.avatarUrl ?? preset?.avatarUrl ?? null;
  const validPeriodEnd = ocr?.validPeriodEnd ?? preset?.validPeriodEnd ?? null;
  /**
   * 有服务端矫正裁剪图就用它：本地 objectURL 是未校正的原片，展示会「明明拍正了还是歪的」。
   * 顺序是「本次识别 → 上次回显 → 本地预览」。
   */
  const frontSrc = ocr?.imageUrl ?? preset?.imageUrl ?? frontUrl;
  /** 识别结果那行显示的号码：本次识别有就用它，回显态用输入框里的值（同一张证） */
  const resultIdNo = ocr?.idCardNo ?? (preset ? idCardNo : '');
  /** 输入框里的号码是否就是本次识别出来的那个（回显态不算「刚自动填入」） */
  const idCardAutofilled = Boolean(ocr?.idCardNo) && idCardNo.trim() === ocr?.idCardNo;
  const nameLabel = nameField?.label ?? '姓名';
  const idRequired = idField.required;
  /** 整张卡是否必填：姓名 / 证件号 / 证件类型里任一项必填，就该在卡标题上标星 */
  const cardRequired = Boolean(idRequired || nameField?.required || typeField?.required);
  // 状态胶囊说清「还差哪一面」，别只说「未完成」让人回去数格子
  // （判断用 `frontSrc` 而不是 `frontUrl`：回显态没有本地 objectURL，但图是有的）
  const stateText = verified
    ? '已通过'
    : hasBoth
      ? '已采集'
      : frontSrc
        ? '待补国徽面'
        : backSrc
          ? '待补人像面'
          : '未完成';

  /**
   * 证件类型行 —— 卡的第一行，**只读**。
   *
   * 一期线上链只认居民身份证（OCR 只认身份证；证件号校验是 18 位 + GB 11643 校验位），
   * 所以这里不提供选择，只把已定的事实写出来 —— 留一行是因为后台把它配成了必填字段，
   * 学员看不到它、却要它才能提交，比看见一行「居民身份证」更让人迷惑。
   * 后续真要支持护照 / 港澳台居住证，把这一行换回 Picker 即可，位置和排版都不用动。
   */
  const typeRow = typeField ? (
    <div className="idcard-type">
      <span className="idcard-type__key">
        {typeField.label}
        {typeField.required ? <em className="field__required">*</em> : null}
      </span>
      <span className="idcard-type__value">
        {(typeField.options ?? []).find((option) => option.value === idType)?.label ?? '居民身份证'}
      </span>
    </div>
  ) : null;

  return (
    <Card className="kyc-card idcard-card">
      {/* OCR 要调一次供应商接口（实测 2~4 秒），国徽面裁剪也要一次。
          原先只有卡片底部一行小字，位置在折叠以下、极易被忽略 —— 学员会以为
          「拍完什么都没发生」，于是重复拍，每次都真实计费。 */}
      <ProcessingOverlay
        visible={busy !== null}
        title={busy === 'back' ? '正在裁剪国徽面' : '正在识别身份证'}
        hint={
          busy === 'back'
            ? '正在扶正并去除背景，请勿重复拍摄'
            : '正在识别姓名与证件号，请勿重复拍摄'
        }
      />
      <div className="kyc-card__head">
        <span className="kyc-card__title">
          身份证
          {cardRequired ? <em className="field__required">*</em> : null}
        </span>
        <span className={`kyc-badge${hasBoth || verified ? ' kyc-badge--ok' : ''}`}>
          {stateText}
        </span>
      </div>
      <p className="kyc-card__desc">
        拍摄或上传身份证正反面；两面都会自动裁剪校正，人像面另会识别姓名、证件号、性别、民族并提取证件头像
      </p>

      {typeRow}

      <div className="idcard-slots">
        <CameraCapture
          hint="请将身份证人像面放入取景框"
          mask="id-front"
          allowAlbum
          disabled={disabled || busy !== null}
          triggerClassName={`idcard-slot${frontSrc ? ' idcard-slot--done' : ''}`}
          onCapture={(image) => void doFront(image)}
        >
          {frontSrc ? (
            <>
              <img className="idcard-slot__img" src={frontSrc} alt="身份证人像面" />
              <span className="idcard-slot__tag">人像面 · 重拍</span>
            </>
          ) : (
            <>
              <CameraIcon size={18} />
              <span className="idcard-slot__title">人像面</span>
              <span className="idcard-slot__hint">拍摄或上传</span>
            </>
          )}
        </CameraCapture>

        <CameraCapture
          hint="请将身份证国徽面放入取景框"
          mask="id-back"
          allowAlbum
          disabled={disabled || busy !== null}
          triggerClassName={`idcard-slot${backSrc ? ' idcard-slot--done' : ''}`}
          onCapture={(image) => void doBack(image)}
        >
          {backSrc ? (
            <>
              <img className="idcard-slot__img" src={backSrc} alt="身份证国徽面" />
              <span className="idcard-slot__tag">国徽面 · 重拍</span>
            </>
          ) : (
            <>
              <CameraIcon size={18} />
              <span className="idcard-slot__title">国徽面</span>
              <span className="idcard-slot__hint">拍摄或上传</span>
            </>
          )}
        </CameraCapture>
      </div>

      {failHint ? (
        <Notice tone="danger">
          {failHint.side === 'front' ? '人像面' : '国徽面'}未通过：{failHint.message}
          。请确保证件完整入框、光线充足后重新拍摄或上传。
        </Notice>
      ) : null}

      <div className="idcard-rows">
        <label className="idcard-row">
          <span className="idcard-row__key">
            {nameLabel}
            {nameField?.required ? <em className="field__required">*</em> : null}
          </span>
          <input
            className="idcard-row__input"
            value={name}
            disabled={disabled}
            placeholder={`请输入${nameLabel}`}
            onChange={(event) => onChange({ name: event.target.value })}
          />
          {recognized ? (
            <span className="idcard-row__ok" role="img" aria-label="已识别">
              <CheckIcon size={12} strokeWidth={2.6} />
            </span>
          ) : null}
        </label>
        <label className="idcard-row">
          <span className="idcard-row__key">
            {idField.label}
            {idRequired ? <em className="field__required">*</em> : null}
          </span>
          <input
            className="idcard-row__input mono-num"
            value={idCardNo}
            disabled={disabled}
            maxLength={18}
            placeholder={idField.placeholder ?? '请输入 18 位身份证号'}
            onChange={(event) => onChange({ idCardNo: event.target.value.toUpperCase() })}
          />
        </label>

        {/*
          性别与民族 —— 证件上就印着，跟着这张卡一起出现（见文件头 ⑤）。
          **保留可编辑**：民族是必填项，供应商对生僻族名的识别不稳，
          读不出时这里就是学员唯一的入口；读错时也要能当面改掉。
        */}
        {genderField ? (
          <div className="idcard-row idcard-row--choice">
            <span className="idcard-row__key">
              {genderField.label}
              {genderField.required ? <em className="field__required">*</em> : null}
            </span>
            <span className="idcard-seg">
              {(genderField.options ?? []).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`idcard-seg__option${
                    gender === option.value ? ' idcard-seg__option--on' : ''
                  }`}
                  disabled={disabled}
                  aria-pressed={gender === option.value}
                  onClick={() => {
                    onChange({ gender: option.value });
                    // 学员自己选了就不再是「按证件带入」——提示留着会误导
                    setAutofilled((prev) => ({ ...prev, gender: false }));
                  }}
                >
                  {option.label}
                </button>
              ))}
            </span>
          </div>
        ) : null}
        {ethnicityField ? (
          <label className="idcard-row">
            <span className="idcard-row__key">
              {ethnicityField.label}
              {ethnicityField.required ? <em className="field__required">*</em> : null}
            </span>
            <input
              className="idcard-row__input"
              value={ethnicity}
              disabled={disabled}
              // 最长的民族称谓「乌孜别克族」5 个字，20 够用；限长是为了挡住
              // 「把住址误识别进这一格」那种明显不合理的值
              maxLength={20}
              placeholder={ethnicityField.placeholder ?? '如：汉族'}
              onChange={(event) => {
                onChange({ ethnicity: event.target.value });
                setAutofilled((prev) => ({ ...prev, ethnicity: false }));
              }}
            />
          </label>
        ) : null}
      </div>

      {/* 带入就要说出来是哪几项：静默填一个值，学员会当成自己填的 */}
      {autofilled.gender || autofilled.ethnicity ? (
        <p className="kyc-note">
          {[
            autofilled.gender ? genderField?.label : null,
            autofilled.ethnicity ? ethnicityField?.label : null,
          ]
            .filter((label): label is string => Boolean(label))
            .join('、')}
          已按证件识别结果带入，与证件不符可直接修改
        </p>
      ) : null}

      {recognized ? (
        // 识别成功区：左侧是**服务端切好的头像**（④ 比对的参照图），
        // 右侧是证件号 / 有效期 / 质量标签。头像没切出来时给一个缺口占位，
        // 让"没提取到"这件事看得见，而不是悄悄不显示。
        <div className="idcard-result">
          {avatarUrl ? (
            // 头像切图只有 44×56，学员没法确认「切的是不是我的脸、有没有歪」。
            // 它同时是 ④ 比对的参照图，切错了要当场发现，不能等比对掉分才回头找。
            <button
              type="button"
              className="zoom-hit"
              aria-label="放大查看证件头像"
              onClick={() => openImagePreview(avatarUrl)}
            >
              <img className="idcard-result__avatar" src={avatarUrl} alt="证件头像切图" />
              <span className="zoom-hit__badge">
                <SearchIcon size={10} strokeWidth={2.6} />
              </span>
            </button>
          ) : (
            <span className="idcard-result__avatar idcard-result__avatar--missing">
              <PortraitIcon size={20} />
            </span>
          )}
          <div className="idcard-result__body">
            <p className="idcard-result__line">
              识别结果 <b className="mono-num">{resultIdNo || '—'}</b>
            </p>
            {validPeriodEnd ? (
              <p className="idcard-result__line">有效期至 {validPeriodEnd}</p>
            ) : null}
            {presentation && presentation.chips.length > 0 ? (
              <div className="idcard-result__chips">
                {presentation.chips.map((chip) => (
                  <span key={chip} className="idcard-result__chip">
                    {chip}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="kyc-note">姓名与证件号可与识别结果核对，以证件为准</p>
      )}

      {presentation?.warnings.map((warning) => (
        <div key={warning} className="field-hint field-hint--warn">
          ⓘ {warning}
        </div>
      ))}

      {recognized ? (
        <p className="kyc-note">
          {idCardAutofilled
            ? '已自动填入姓名与证件号；与证件不符时可直接修改'
            : '以实际证件为准，请核对上方号码'}
        </p>
      ) : null}
      {hints?.map((hint) => (
        <div key={hint} className="field-hint">
          ⓘ {hint}
        </div>
      ))}

      {nameError ? <div className="field-error">{nameError}</div> : null}
      {idError ? <div className="field-error">{idError}</div> : null}
      {genderError ? <div className="field-error">{genderError}</div> : null}
      {ethnicityError ? <div className="field-error">{ethnicityError}</div> : null}
    </Card>
  );
}
